/**
 * Lazy access to the full XCSoar glider database. The ~125 KB JSON is loaded
 * via dynamic `import()` only when first needed (picker opened, or an unknown
 * glider id in the URL hash) so it stays out of the initial bundle.
 */
import type { Glider } from "./glider";

let cache: readonly Glider[] | null = null;

export const loadGliderDb = async (): Promise<readonly Glider[]> => {
  if (cache !== null) return cache;
  const mod = await import("../data/gliders.json");
  // reason: gliders.json is generated by scripts/import-xcsoar.ts to match the
  // Glider shape exactly; the JSON-inferred type widens the polarPoints tuple
  // and the `source` literal, so one assertion at this boundary is correct.
  cache = mod.default as unknown as readonly Glider[];
  return cache;
};

export const findGlider = (
  gliders: readonly Glider[],
  id: string,
): Glider | undefined => gliders.find((g) => g.id === id);

/** Case-insensitive substring match on manufacturer or model. */
export const filterGliders = (
  gliders: readonly Glider[],
  query: string,
): readonly Glider[] => {
  const q = query.trim().toLowerCase();
  if (q === "") return gliders;
  return gliders.filter(
    (g) =>
      g.manufacturer.toLowerCase().includes(q) ||
      g.model.toLowerCase().includes(q) ||
      g.displayName.toLowerCase().includes(q),
  );
};
